const moneyNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const paidBookingStatuses = ['PAID', 'COMPLETE', 'COMPLETED'];
const paidEatStatuses = ['DELIVERED', 'COMPLETE', 'COMPLETED'];

export const formatAdminMoney = (value, settings = {}) => {
  const decimals = Number.isFinite(Number(settings.decimal)) ? Number(settings.decimal) : 2;
  const amount = moneyNumber(value).toLocaleString(settings.country === 'Vietnam' ? 'vi-VN' : 'fr-FR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
  const symbol = settings.symbol || '€';
  return settings.swipe_symbol === false ? `${symbol} ${amount}` : `${amount} ${symbol}`;
};

export const getSettlementTone = (balance) => {
  if (balance < -0.005) return 'driver_owes';
  if (balance > 0.005) return 'platform_owes';
  return 'settled';
};

export const getSettlementLabel = (balance) => {
  const tone = getSettlementTone(balance);
  if (tone === 'driver_owes') return 'Chauffeur doit';
  if (tone === 'platform_owes') return 'À payer';
  return 'Soldé';
};

export const getSettlementStyle = (balance) => {
  const tone = getSettlementTone(balance);
  if (tone === 'driver_owes') return { color: '#b42318', backgroundColor: '#fff1f0', borderColor: '#fecdca' };
  if (tone === 'platform_owes') return { color: '#027a48', backgroundColor: '#ecfdf3', borderColor: '#abefc6' };
  return { color: '#475467', backgroundColor: '#f2f4f7', borderColor: '#eaecf0' };
};

export const getVtcSettlement = (bookings = [], userId) => {
  const rows = bookings.filter(booking => {
    const status = String(booking.status || '').toUpperCase();
    return paidBookingStatuses.includes(status) && (booking.driver === userId || booking.driverId === userId || booking.driverUid === userId);
  });

  return rows.reduce((totals, booking) => {
    const driverShare = moneyNumber(booking.driver_share || booking.driverShare || booking.driverEarning);
    const tips = moneyNumber(booking.tipamount || booking.tipAmount);
    const platformCommission = Math.max(0, moneyNumber(booking.convenience_fees || booking.convenienceFee) - moneyNumber(booking.discount));
    const cashCollected = moneyNumber(booking.cashPaymentAmount || (String(booking.payment_mode || '').toLowerCase() === 'cash' ? booking.trip_cost || booking.tripCost || booking.payableAmount : 0));
    const driverOwes = cashCollected > 0 ? platformCommission : 0;
    const platformOwes = cashCollected > 0 ? 0 : driverShare + tips;
    return {
      driverOwes: totals.driverOwes + driverOwes,
      platformOwes: totals.platformOwes + platformOwes,
      commission: totals.commission + platformCommission,
      gross: totals.gross + moneyNumber(booking.trip_cost || booking.tripCost || booking.payableAmount),
      count: totals.count + 1,
      lastActivity: Math.max(totals.lastActivity, moneyNumber(booking.tripdate || booking.tripDate || booking.updatedAt || booking.createdAt))
    };
  }, { driverOwes: 0, platformOwes: 0, commission: 0, gross: 0, count: 0, lastActivity: 0 });
};

export const withVtcSettlements = (users = [], bookings = []) => users.map(user => {
  const settlement = getVtcSettlement(bookings, user.id || user.uid);
  const balance = settlement.platformOwes - settlement.driverOwes;
  return {
    ...user,
    settlement,
    settlementBalance: balance,
    settlementLabel: getSettlementLabel(balance),
    driverOwesAmount: settlement.driverOwes,
    platformOwesAmount: settlement.platformOwes,
    accountingGrossAmount: settlement.gross,
    accountingCommissionAmount: settlement.commission,
    accountingRideCount: settlement.count,
    accountingLastActivity: settlement.lastActivity
  };
});

export const getEatCourierSettlement = (orders = [], courierId) => {
  return orders
    .filter(order => paidEatStatuses.includes(String(order.status || '').toUpperCase()) && (order.courierId === courierId || order.courier === courierId || order.driver === courierId))
    .reduce((totals, order) => ({
      platformOwes: totals.platformOwes + moneyNumber(order.pricing?.courierEarning),
      count: totals.count + 1,
      lastActivity: Math.max(totals.lastActivity, moneyNumber(order.deliveredAt || order.updatedAt || order.createdAt))
    }), { platformOwes: 0, count: 0, lastActivity: 0 });
};

export const getEatRestaurantSettlement = (orders = [], restaurantId) => {
  return orders
    .filter(order => paidEatStatuses.includes(String(order.status || '').toUpperCase()) && (order.restaurantId === restaurantId || order.restaurant_id === restaurantId))
    .reduce((totals, order) => {
      const restaurantNet = moneyNumber(order.pricing?.restaurantNet ?? order.pricing?.restaurantSubtotal);
      const platformNet = moneyNumber(order.pricing?.platformNet ?? order.pricing?.platformFee);
      return {
        platformOwes: totals.platformOwes + restaurantNet,
        commission: totals.commission + platformNet,
        count: totals.count + 1,
        lastActivity: Math.max(totals.lastActivity, moneyNumber(order.deliveredAt || order.updatedAt || order.createdAt))
      };
    }, { platformOwes: 0, commission: 0, count: 0, lastActivity: 0 });
};

export const getAccountingSummary = (driverRows = [], courierRows = [], restaurantRows = []) => {
  const driverOwes = driverRows.reduce((total, row) => total + moneyNumber(row.driverOwesAmount), 0);
  const platformOwesDrivers = driverRows.reduce((total, row) => total + moneyNumber(row.platformOwesAmount), 0);
  const platformOwesCouriers = courierRows.reduce((total, row) => total + moneyNumber(row.platformOwesAmount), 0);
  const platformOwesRestaurants = restaurantRows.reduce((total, row) => total + moneyNumber(row.platformOwesAmount), 0);
  return {
    driverOwes,
    platformOwesDrivers,
    platformOwesCouriers,
    platformOwesRestaurants,
    platformOwesTotal: platformOwesDrivers + platformOwesCouriers + platformOwesRestaurants,
    attentionCount: driverRows.filter(row => moneyNumber(row.driverOwesAmount) > moneyNumber(row.platformOwesAmount)).length
  };
};
